/**
 * Add listeners to `window` to know when focus is gained or lost.
 * Callbacks are then invoked accordingly.
 * When invoked, the current status of `window` is checked to invoke
 * the correct callback.
 *
 * @param {function} whenFocused - Invoked when the page gains focus
 * @param {function} whenBlurred - Invoked when the page loses focus
 * @return {function} A function to call to unsubscribe the listeners.
 */
function addListener({ whenFocused, whenBlurred }) {
	window.addEventListener("focus", whenFocused);
	window.addEventListener("blur", whenBlurred);

	if (document.hasFocus()) {
		whenFocused();
	} else {
		whenBlurred();
	}

	return () => {
		window.removeEventListener("focus", whenFocused);
		window.removeEventListener("blur", whenBlurred);
	};
}

/**
 * Listen to window focus and resolve when the page had focus for `time` ms.
 * This may never resolve and never reject.
 *
 * @param {number} time - Time in millisecond. As it's ultimately passed to `setTimeout` the same time restrictions apply
 * @return {Promise} Resolve when the page had focus continuously.
 */
export function hasFocusFor(time) {
	return new Promise((resolve, reject) => {
		if (!time) {
			reject(new Error("`time` must be provided"));
		}

		let timer = null;

		const unsubscribe = addListener({
			whenFocused: () => {
				if (timer) {
					return;
				}

				timer = setTimeout(() => {
					resolve();
					unsubscribe();
				}, time);
			},
			whenBlurred: () => {
				clearTimeout(timer);
			},
		});
	});
}
